import axios, { AxiosHeaders, type AxiosAdapter, type AxiosResponse } from 'axios'

import { handleMockRequest, type MockRequest } from './handlers'

import { API_BASE_URL, http } from '@/api/client'

/**
 * Opt-in mock API.
 *
 * Installed ONLY when `VITE_USE_MOCK_API=true`. It is implemented as an axios adapter
 * rather than a browser service worker so that it is fully typed, testable, and cannot
 * accidentally survive into a production bundle behind a stale registration — the
 * decision is made once, at boot, from an environment variable.
 *
 * When it is active the app also pins a permanent amber "Mock API — data is synthetic"
 * banner, so nobody can mistake fabricated data for a live tenant.
 */

export const MOCK_API_ENABLED = import.meta.env.VITE_USE_MOCK_API === 'true'

/** Simulated round-trip latency, so loading states are actually visible in dev. */
const LATENCY_MS = 220

/** `/api/v1/cameras/abc?x=1` (or an absolute URL) -> `/cameras/abc`. */
export function stripBase(url: string): string {
  let path = url
  if (path.startsWith('http')) path = path.slice(new URL(path).origin.length)
  if (API_BASE_URL && path.startsWith(API_BASE_URL)) path = path.slice(API_BASE_URL.length)
  return path.split('?')[0] || '/'
}

function parseBody(data: unknown): unknown {
  if (typeof data !== 'string') return data
  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}

const mockAdapter: AxiosAdapter = async (config) => {
  const path = stripBase(config.url ?? '')

  const query = new URLSearchParams()
  const params = config.params as Record<string, unknown> | undefined
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) query.set(key, String(value))
    }
  }
  const inlineQuery = (config.url ?? '').split('?')[1]
  if (inlineQuery) {
    for (const [key, value] of new URLSearchParams(inlineQuery)) query.set(key, value)
  }

  const request: MockRequest = {
    method: config.method ?? 'get',
    path,
    query,
    body: parseBody(config.data),
  }

  await new Promise((resolve) => setTimeout(resolve, LATENCY_MS))

  const result = await handleMockRequest(request)

  const response: AxiosResponse = {
    data: result.data,
    status: result.status,
    statusText: result.status >= 400 ? 'Error' : 'OK',
    headers: AxiosHeaders.from(result.headers ?? { 'content-type': 'application/json' }),
    config,
    request: null,
  }

  if (result.status >= 400) {
    throw new axios.AxiosError(
      `Request failed with status code ${result.status}`,
      String(result.status),
      config,
      null,
      response,
    )
  }

  return response
}

export function installMockApi(): void {
  if (!MOCK_API_ENABLED) return
  http.defaults.adapter = mockAdapter
  // The silent-refresh call in `api/client.ts` uses the bare axios instance.
  axios.defaults.adapter = mockAdapter
  console.warn(
    '[U-Observe] Mock API is ENABLED (VITE_USE_MOCK_API=true). All data is synthetic.',
  )
}
